/*
 * FLOW LINES AS RIBBONS: the pure part (no DOM, no d3), so every rule can be tested. Chris, 2026-10-18: "I would like to see if the flow lines MIGHT BE better with the ribbon technology with
 * small flowing dots or something like that to show weight... the ribbon might be better and better to hover over."
 *
 * It is a second STYLE of the existing flow lines, drawn from the SAME counts (clusterService's pathWeights / directionalPathWeights), so nothing new is measured. What the throwaway mock of
 * 2026-10-18 (docs/mocks/flow-ribbons-*.png, docs/FUNNEL_TASKS.md #18) taught, and what is built from it:
 *   - every pair at once is a hairball, the busiest routes READ: only the top N are drawn (the Display setting offers 10, 20 or all; 20 is the default);
 *   - the width says how much, so does the number of dots, and the dots all flow at ONE pace (a pace that varied with the traffic read as noise): more traffic = a wider ribbon AND more dots;
 *   - the ribbon runs from the edge of one box to the edge of the next, behind the boxes, exactly like the funnel's (funnelRibbons.ts does the layout);
 *   - the dots flow the way MORE of the traffic went (the counts merge both directions of a pair into one line, as the dashed lines do).
 * A ribbon is a constant width, with no taper: in the funnel a narrowing means visitors were lost, and a flow does not lose anyone, so a taper here would say something false.
 */
import { Box, CentreSample, layoutRibbons, ribbonWidth, RibbonShape } from "./funnelRibbons";

export type FlowStyle = "lines" | "ribbons";

/** How many flows are drawn as ribbons: the busiest ones. The Display settings offer these; 0 means "all of them". */
export const FLOW_RIBBON_COUNT_OPTIONS: number[] = [10, 20, 0];
export const FLOW_RIBBON_TOP_N = 20;
/** A flow ribbon's width as a fraction of the funnel ribbon's for the same share of traffic: ten of them share the grid. */
export const FLOW_WIDTH_SCALE = 0.55;
export const MIN_DOTS = 2;
export const MAX_DOTS = 16;
/** pixels per second, the same for every ribbon */
export const DOT_PACE = 70;
export const DOT_RADIUS = 2.4;
/** the dots keep to the middle of the band, never on its edge */
export const LANE_SPREAD = 0.62;

const PAIR_KEY = /^(\d+)-(\d+)$/;
const ARRIVAL_KEY = /^arrival:(\d+)$/;

/**
 * The busiest `n` of the entries (`n` 0 = all of them), busiest first (ties by key, so the choice never flickers). A flow is either a route between two boxes ("3-5") or an ARRIVAL ("arrival:3":
 * visitors landing on a page, drawn from the spawn point where new visitors appear). An arrival is a ribbon like any other (Chris, 2026-10-18: "We're not showing the ribbons/values from spawn?").
 */
export const pickTop = (entries: Array<[string, number]>, n: number = FLOW_RIBBON_TOP_N): Array<[string, number]> =>
  entries
    .filter(([key, weight]) => (PAIR_KEY.test(key) || ARRIVAL_KEY.test(key)) && weight > 0)
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
    .slice(0, n > 0 ? n : undefined);

/** Which way MORE of a pair's traffic went. The key is "lo-hi"; a tie, or no recorded direction, reads lo to hi (the same rule the dashed lines use). */
export const directionOf = (key: string, directional: Map<string, number>): [number, number] | null => {
  const m = PAIR_KEY.exec(key);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = Number(m[2]);
  const loToHi = directional.get(`${lo}->${hi}`) || 0;
  const hiToLo = directional.get(`${hi}->${lo}`) || 0;

  return hiToLo > loToHi ? [hi, lo] : [lo, hi];
};

export interface FlowRibbon {
  key: string;
  weight: number;
  /** the box index it leaves, or null for an arrival (it leaves the spawn point) */
  from: number | null;
  to: number;
  shape: RibbonShape;
  /** the ribbon's width in pixels */
  width: number;
  /** the length of the centre line, in pixels */
  length: number;
  /** the weight this ribbon's width, colour and dots are measured against: the busiest ribbon drawn, or (with one page focused) the busiest flow of the whole site, so a page's flows are not inflated */
  refWeight: number;
}

export interface FlowInput {
  entries: Array<[string, number]>;
  directional: Map<string, number>;
  boxFor: (index: number) => Box | null;
  /** where new visitors appear (a thin box at the grid's left edge): the start of every arrival ribbon. Without one, arrivals are not drawn. */
  spawn?: Box | null;
  boxH: number;
  /**
   * Flows to draw WHATEVER their rank (they are added to the busiest `topN`, not counted in it). The ones that touch the goal page: only a few visits in a hundred get there, so they are
   * exactly the ones a top-N cuts (Chris, 2026-10-18: "There's no flow line ribbon showing up from any page slot box to /order-confirmed?!"). A kept flow still has to be drawable
   * (both of its ends on the grid), and it does not change the scale the others are drawn on.
   */
  keep?: (flow: { key: string; from: number | null; to: number }) => boolean;
  /** how many to draw (0 = all); the default is FLOW_RIBBON_TOP_N */
  topN?: number;
  /**
   * The weight that gets the widest ribbon. Absent: the busiest ribbon drawn. With one page focused only that page's flows are drawn, so "the busiest drawn" would blow its busiest flow up to the
   * widest ribbon there is (a ribbon far wider than the channel between two rows) for a flow that is a few per cent of the site's traffic: the focus passes the whole site's busiest instead, so a
   * ribbon is exactly as wide, and as dark, as it was before the focus.
   */
  widthMax?: number;
  /** every box on the grid: with these, a ribbon is never wrapped onto an edge that has no box beside it to run past (the top of the top row, the bottom of the bottom row, the last column's outer edge) */
  allBoxes?: Box[];
}

export const lengthOf = (centre: CentreSample[]): number => {
  let total = 0;
  for (let i = 1; i < centre.length; i++) total += Math.hypot(centre[i].x - centre[i - 1].x, centre[i].y - centre[i - 1].y);

  return total;
};

/** The distance along the centre line at each sample (the first is 0, the last is the whole length). The samples are even in the curve's own parameter, NOT in distance. */
export const cumulativeLengths = (centre: CentreSample[]): number[] => {
  const cum: number[] = [];
  let total = 0;
  for (let i = 0; i < centre.length; i++) {
    if (i > 0) total += Math.hypot(centre[i].x - centre[i - 1].x, centre[i].y - centre[i - 1].y);
    cum.push(total);
  }

  return cum;
};

/**
 * The ribbons to draw, with the width of each set by its share of the BUSIEST ribbon DRAWN (the same square-root scale as the funnel's). A flow that cannot be drawn (a box that is not on the
 * grid, no spawn point for an arrival) is dropped BEFORE the busiest N are chosen, so it neither takes one of the N places nor sets the scale for the others.
 */
export const layoutFlow = (input: FlowInput): FlowRibbon[] => {
  const prepared: Array<{ key: string; weight: number; from: number | null; to: number; a: Box; b: Box; arrival: boolean }> = [];
  pickTop(input.entries, 0).forEach(([key, weight]) => {
    const arrival = ARRIVAL_KEY.exec(key);
    let from: number | null;
    let to: number;
    let a: Box | null;
    let b: Box | null;
    if (arrival) {
      from = null;
      to = Number(arrival[1]);
      a = input.spawn ?? null;
      b = input.boxFor(to);
    } else {
      const dir = directionOf(key, input.directional);
      if (!dir) return;
      [from, to] = dir;
      a = input.boxFor(dir[0]);
      b = input.boxFor(dir[1]);
    }
    if (!a || !b || a.key === b.key) return;
    prepared.push({ key, weight, from, to, a, b, arrival: !!arrival });
  });
  const n = input.topN === undefined ? FLOW_RIBBON_TOP_N : input.topN;
  const ranked = n > 0 ? prepared.slice(0, n) : prepared;
  const kept = n > 0 && input.keep ? prepared.slice(n).filter((p) => input.keep!({ key: p.key, from: p.from, to: p.to })) : [];
  const drawn = [...ranked, ...kept];
  if (!drawn.length) return [];
  const max = Math.max(drawn[0].weight, input.widthMax ?? 0);
  // the room between the spawn bar and the nearest column of boxes (only when the grid is known)
  const spawnBox = input.spawn ?? null;
  const gutter = spawnBox && input.allBoxes && input.allBoxes.length ? Math.min(...input.allBoxes.map((b) => b.x - b.w / 2 - (spawnBox.x + spawnBox.w / 2)).filter((g) => g > 0)) : undefined;
  const specs = drawn.map((p) => {
    const w = ribbonWidth(p.weight, max, input.boxH) * FLOW_WIDTH_SCALE;

    // an arrival always leaves the spawn point's right edge and arrives at the left edge of its page: the spawn is at the grid's left margin, so the way is always rightwards (the
    // edges that "face" each other would send one to the top or the bottom of the thin spawn box, depending on how far up or down its page is)
    // The spawn point sits in the grid's left margin, so the gap between it and the first column's boxes is only a few pixels: a handle of the usual length (45% of the distance, which here is
    // mostly the way UP or DOWN) swung the ribbon out past the left edge of the grid in a big "C". Half the gap, at most, keeps the bend inside it.
    const gap = p.b.x - p.b.w / 2 - (p.a.x + p.a.w / 2);

    // an arrival climbs (or drops) the gutter beside the spawn bar, half way across it, and then runs along its page's row (channelOutline's `mid`); the gutter is as wide as the nearest column's left
    // edge is from the bar (a far page must not drag the climb out into the grid)
    const mid = p.arrival ? Math.max(0, (gutter ?? gap) / 2) : undefined;

    return { id: p.key, from: p.a, to: p.b, startW: w, endW: w, ...(p.arrival ? { sides: { from: "r" as const, to: "l" as const }, handle: Math.max(6, Math.min(60, gap * 0.5)), mid } : {}) };
  });
  // an edge is open when a box lies beyond it: a row above (top), a row below (bottom); the side edges are always open (the spawn gutter is at the left, and a ribbon only wraps ONTO a side edge when its
  // partner is that way, so the last column's outer edge is never asked for)
  const boxes = input.allBoxes;
  const edgeOpen = boxes
    ? (key: string, side: "l" | "r" | "t" | "b"): boolean => {
        const me = boxes.find((b) => b.key === key);
        if (!me) return true;
        if (side === "t") return boxes.some((b) => b.y < me.y - 0.6 * me.h);
        if (side === "b") return boxes.some((b) => b.y > me.y + 0.6 * me.h);

        return true;
      }
    : undefined;
  const shapes = layoutRibbons(specs, { wrap: true, edgeOpen });

  return drawn.map((p, i) => ({ key: p.key, weight: p.weight, from: p.from, to: p.to, shape: shapes[i], width: specs[i].startW, length: lengthOf(shapes[i].centre), refWeight: max }));
};

/** An SVG path along a ribbon's centre line: what the invisible hover target follows. */
export const centrePath = (centre: CentreSample[]): string => centre.map((c, i) => `${i ? "L" : "M"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join("");

// ── the dots ──────────────────────────────────────────────────────────────

export interface Dot {
  key: string;
  i: number;
  /** where along the ribbon it starts, 0 to 1 */
  phase: number;
  /** across the band, -1 to 1 */
  lane: number;
}

/** A small deterministic hash (FNV-1a), so the same ribbon always gets the same dots and a redraw never makes them jump. */
const hash = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }

  return h >>> 0;
};

/** MORE traffic = MORE dots, on the same square-root scale as the width, between MIN_DOTS and MAX_DOTS. */
export const dotCount = (weight: number, maxWeight: number): number => {
  if (!(maxWeight > 0) || !(weight > 0)) return 0;
  const share = Math.min(1, weight / maxWeight);

  return Math.round(MIN_DOTS + (MAX_DOTS - MIN_DOTS) * Math.sqrt(share));
};

const GOLDEN = 0.6180339887498949;

/**
 * The dots of one ribbon, spread evenly along it (so the flow looks calm) at lanes spread across the band. A dot's place depends ONLY on its ribbon and its number, never on how many dots
 * there are: the golden-ratio sequence is evenly spread for ANY count, and each ribbon starts it from its own offset. (A dot's phase used to be i/n, so every time a ribbon gained a dot as
 * traffic grew, every dot on it jumped to a new place at once.)
 */
export const dotsFor = (key: string, weight: number, maxWeight: number): Dot[] => {
  const n = dotCount(weight, maxWeight);
  const offset = (hash(`${key}@`) % 1000) / 1000;

  return Array.from({ length: n }, (_, i) => ({ key, i, phase: (offset + i * GOLDEN) % 1, lane: (hash(`${key}~${i}`) % 2001) / 1000 - 1 }));
};

/**
 * Where a dot is `timeMs` into the animation: the same pace for every ribbon, so a longer ribbon takes longer to cross. The pace is in pixels along the ribbon's centre line, so the dot is placed
 * by DISTANCE (`cum`, from cumulativeLengths). Without it a dot was placed by sample number, and the samples of a bezier are bunched near its ends and far apart in the middle: a dot
 * crept out of a box and raced across the gap (measured: ribbons running at 63 to 108 px/s while all were meant to run at 70).
 */
export const dotAt = (centre: CentreSample[], length: number, dot: Dot, timeMs: number, cum?: number[]): { x: number; y: number } => {
  const n = centre.length - 1;
  if (n < 1) return { x: centre[0]?.x ?? 0, y: centre[0]?.y ?? 0 };
  const travelled = length > 0 ? (timeMs / 1000) * (DOT_PACE / length) : 0;
  const u = (((dot.phase + travelled) % 1) + 1) % 1;
  let i: number;
  let f: number;
  if (cum && cum[n] > 0) {
    const distance = u * cum[n];
    i = 0;
    while (i < n - 1 && cum[i + 1] <= distance) i++;
    const span = cum[i + 1] - cum[i];
    f = span > 0 ? (distance - cum[i]) / span : 0;
  } else {
    const at = u * n;
    i = Math.min(n - 1, Math.floor(at));
    f = at - i;
  }
  const a = centre[i];
  const b = centre[i + 1];
  const x = a.x + (b.x - a.x) * f;
  const y = a.y + (b.y - a.y) * f;
  const nx = a.nx + (b.nx - a.nx) * f;
  const ny = a.ny + (b.ny - a.ny) * f;
  const half = a.half + (b.half - a.half) * f;
  const off = dot.lane * half * LANE_SPREAD;

  return { x: x + nx * off, y: y + ny * off };
};

/*
 * HOW MUCH A RIBBON LOOKS LIKE IT CARRIES (2026-10-19). Chris, looking at the crowded channel: "I wonder if we could do additional visual gimmicks to show or highlight that one connection between
 * pages is MORE than another beyond the width, maybe with color and/or transparency."
 *
 * The width, the number of dots and now the colour all say the same one thing, more traffic, so a person can read it any of three ways. The ribbon's own COLOUR deepens and its fill gets more
 * solid with its share of the busiest ribbon drawn: a trickle is a pale, nearly clear band, the busiest route a deep, solid one. ONE hue on purpose: a change of hue says "a different kind of
 * thing" (and the funnel and the hover already use their own colours), a change of depth says "more of the same". The share is raised to a power below one, so the long tail of small routes
 * stays visible instead of all of them fading to nothing next to one giant. Every ribbon also gets a thin outline a little stronger than its fill: where ten translucent bands cross, the fills
 * pile up into one dark patch that no longer says which band is which, and the outlines are what keep each one's edges.
 */
export const PAINT_EXPONENT = 0.65;
const PALE = { r: 140, g: 165, b: 210, fill: 0.16, edge: 0.3 };
const DEEP = { r: 25, g: 55, b: 185, fill: 0.62, edge: 0.85 };
/** every ribbon but the one being looked at, while one is hovered */
export const DIMMED_OPACITY = 0.22;
export const HOVER_FILL = "rgba(30, 70, 210, 0.78)";
export const HOVER_EDGE = "rgba(20, 45, 150, 1)";

/** 0 for no traffic, 1 for the busiest ribbon drawn */
export const paintShare = (weight: number, max: number): number => (max > 0 && weight > 0 ? Math.min(1, Math.pow(weight / max, PAINT_EXPONENT)) : 0);

const mix = (a: number, b: number, t: number): number => a + (b - a) * t;
const rgba = (t: number, alpha: "fill" | "edge"): string => `rgba(${Math.round(mix(PALE.r, DEEP.r, t))}, ${Math.round(mix(PALE.g, DEEP.g, t))}, ${Math.round(mix(PALE.b, DEEP.b, t))}, ${mix(PALE[alpha], DEEP[alpha], t).toFixed(3)})`;

export interface RibbonPaint {
  fill: string;
  edge: string;
  edgeWidth: number;
}

/** The colours of a ribbon at rest, from its share of the busiest ribbon drawn; `hovered` is the one the pointer is on. */
export const ribbonPaint = (weight: number, max: number, hovered = false): RibbonPaint => {
  if (hovered) return { fill: HOVER_FILL, edge: HOVER_EDGE, edgeWidth: 1.6 };
  const t = paintShare(weight, max);

  return { fill: rgba(t, "fill"), edge: rgba(t, "edge"), edgeWidth: 0.8 };
};
